import { useEffect, useRef, useState } from "react";
import { Camera, FileText, ImagePlus, Loader2 } from "lucide-react";
import { isNativeApp } from "../lib/platform";
import { captureProfileGuard } from "../lib/profileSession";
import { IDENTITY_DOCUMENT_ACCEPT, MAX_IDENTITY_DOCUMENT_BYTES } from "../lib/identityDocumentFiles";
import { pickIdentityPhoto, type IdentityPhotoSource } from "../lib/identityPhoto";
import { Button } from "./beui/Button";
import { Modal } from "./beui/Modal";
import { AttachmentUpload } from "./motion/attachment-upload";

/** Source selection owns no saved documents. Only the form's acknowledged upload
 * changes completion; dismissed or stale native results cannot reach onFile. */
export function IdentityDocumentPicker({ label, disabled, hasSelection, replacing, onFile, onError, onBusyChange }: {
  label: string;
  disabled: boolean;
  hasSelection: boolean;
  replacing: boolean;
  onFile: (file: File) => void;
  onError: (message: string) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [open, setOpen] = useState(() => isNativeApp() && replacing && !disabled);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => { pending.current?.abort(); }, []);

  async function choosePhoto(source: IdentityPhotoSource) {
    if (pending.current || disabled) return;
    const controller = new AbortController();
    const assertProfile = captureProfileGuard();
    const assertCurrent = () => {
      assertProfile();
      if (controller.signal.aborted) throw new DOMException("Selection closed.", "AbortError");
    };
    pending.current = controller;
    setPreparing(true); setError(null); onBusyChange(true);
    try {
      const file = await pickIdentityPhoto(source, assertCurrent, controller.signal);
      assertCurrent();
      if (file) { setOpen(false); onFile(file); }
    } catch (failure) {
      try { assertCurrent(); } catch { return; }
      setError(failure instanceof Error ? failure.message : "Could not open this source. Try another source or choose a file.");
    } finally {
      if (pending.current === controller) pending.current = null;
      if (!controller.signal.aborted) { setPreparing(false); onBusyChange(false); }
    }
  }

  if (!isNativeApp()) return <AttachmentUpload value={[]} multiple={false} maxFiles={1} maxFileSize={MAX_IDENTITY_DOCUMENT_BYTES}
    accept={IDENTITY_DOCUMENT_ACCEPT} disabled={disabled} inputLabel={`Upload ${label}`}
    title={hasSelection ? "Choose a different file" : "Choose a file or drop it here"} description="JPEG, PNG, WebP or PDF · up to 25 MB"
    onFilesAdded={(_items, incoming) => { if (incoming[0]) onFile(incoming[0]); }}
    onFilesRejected={(_incoming, reason) => onError(reason === "too-large" ? "Choose a file up to 25 MB." : "Choose one document at a time.")}
    classNames={{ dropzone: "identity-dropzone" }} />;

  return <>
    <Button variant="outline" className="w-full min-h-11" disabled={disabled} aria-label={`Upload ${label}`} onClick={() => { setOpen(true); setError(null); }}>
      <ImagePlus className="w-4 h-4" aria-hidden="true" />{hasSelection ? "Choose a different document" : "Upload document"}
    </Button>
    <p className="text-xs text-mut mt-2">Gallery, camera or file · JPEG, PNG, WebP or PDF · up to 25 MB</p>
    <input ref={fileInput} type="file" hidden accept={IDENTITY_DOCUMENT_ACCEPT} aria-label={`Choose file for ${label}`} disabled={disabled}
      onChange={event => { const file = event.target.files?.[0]; event.target.value = ""; if (file) onFile(file); }} />
    {open && <Modal label={`Upload ${label}`} className="identity-source-dialog" dismissible={!preparing} onClose={() => setOpen(false)}>
      <div className="p-5 space-y-4 overflow-auto">
        <h3 className="font-bold text-navy">Upload {label}</h3>
        <p className="text-sm text-mut">Choose one source. Keep the whole document clear and readable.</p>
        <div className="flex flex-col gap-3">
          <Button variant="outline" className="min-h-11 justify-start" disabled={preparing} onClick={() => void choosePhoto("gallery")}><ImagePlus className="w-4 h-4" aria-hidden="true" />Choose from gallery</Button>
          <Button variant="outline" className="min-h-11 justify-start" disabled={preparing} onClick={() => void choosePhoto("camera")}><Camera className="w-4 h-4" aria-hidden="true" />Take photo</Button>
          <Button variant="outline" className="min-h-11 justify-start" disabled={preparing} onClick={() => { fileInput.current?.click(); setOpen(false); }}><FileText className="w-4 h-4" aria-hidden="true" />Choose file</Button>
        </div>
        {preparing && <p className="text-sm text-mut flex gap-2 items-center" role="status"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Waiting for your photo…</p>}
        {error && <p className="text-sm text-brand-red" role="alert">{error}</p>}
        <Button variant="ghost" className="min-h-11" disabled={preparing} onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </Modal>}
  </>;
}
